<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { CountryFlag, Size } from '~/types/custom'
import type { Flag } from '~/types/assets'
import { Iconify } from '~/types/assets'
import { localeCodes } from '~/config/locales'
import { useRoot } from '~/store'

const locales: ComputedRef<Flag[]> = computed(() =>
  localeCodes.map(
    (locale: string): Flag => ({
      countryCode: locale,
      src: CountryFlag[locale as keyof typeof CountryFlag]
    })
  )
)

const { themes } = storeToRefs(useRoot())
</script>

<template lang="pug">
KinesisContainer.portfolio__beta(
  tag="section"
)
  KinesisElement.portfolio__beta__locales(
    :strength="25"
    type="depth"
  )
    h2 8 {{ $t('portfolio.beta.languages') }}
    ul
      li(
        v-for="(flag, index) in locales"
        :key="`locale-${index}`"
      )
        IconFlag(
          :flag="flag",
          :size="Size.md",
        )
  KinesisElement.portfolio__beta__themes.pb-4(
    :strength="25"
    type="depth"
  )
    h3 6 {{ $t('portfolio.beta.themes') }}
    ul
      li(
        v-for="(theme, index) in themes"
        :key="`theme-${index}`"
      )
        IconTheme(
          :theme="theme",
          :size="Size.md",
        )
  KinesisElement.portfolio__beta__features(
    :strength="25"
    type="depth"
  )
    h4 {{ $t('portfolio.beta.features.title') }}
    .portfolio__beta__features__lists
      ul
        li {{ $t('portfolio.beta.features.items.0') }}
        li {{ $t('portfolio.beta.features.items.1') }}
        li {{ $t('portfolio.beta.features.items.2') }}
        li {{ $t('portfolio.beta.features.items.3') }}
      ul
        li {{ $t('portfolio.beta.features.items.4') }}
        li {{ $t('portfolio.beta.features.items.5') }}
        li {{ $t('portfolio.beta.features.items.6') }}
        li {{ $t('portfolio.beta.features.items.7') }}
  KinesisElement.portfolio__beta__libraries(
    :strength="25"
    type="depth"
  )
    h5 {{ $t('portfolio.beta.dependencies') }}
    ul
      li
        IconPinia
      li
        Icon(:name="Iconify?.sass")
      li
        Icon(:name="Iconify?.pug")
      li
        Icon(:name="Iconify?.i18n")
      li
        Icon(:name="Iconify?.postcss")
      li
        Icon(:name="Iconify?.unocss")
      li
        Icon(:name="Iconify?.vite")
</template>

<style lang="sass" scoped>
section.portfolio__beta
  @apply w-full h-full flex-center flex-col
  .portfolio__beta__locales,
  .portfolio__beta__themes,
  .portfolio__beta__libraries,
  .portfolio__beta__features
    @apply flex-col flex-center
    h2, h3, h4, h5
      @apply text-lg lg:text-2xl xl:text-4xl text-primary font-bold
    ul
      @apply flex-center gap-4 mt-2
      li
        @apply text-primary text-sm lg:text-lg
  .portfolio__beta__libraries li
    @apply w-fit bg-dark-600 rounded-md p-1 md:p-2 border border-secondary
  .portfolio__beta__features
    @apply pb-4
    .portfolio__beta__features__lists
      @apply flex-center gap-10 bg-transparent-dark p-4 rounded-lg border border-primary mt-4
      ul
        @apply flex flex-col items-start list-inside list-disc gap-1
</style>
